import FilterListIcon from '@mui/icons-material/FilterList'
import {Chip, IconButton} from '@mui/material'
import {
    ColumnHeaderFilterIconButtonProps,
    ColumnHeaderFilterIconButtonPropsOverrides,
    ColumnMenuPropsOverrides,
    DataGrid,
    DataGridProps,
    GridCallbackDetails,
    GridColDef,
    GridColumnHeaderParams,
    GridColumnMenuProps,
    GridFilterItem,
    GridFilterModel,
    GridFilterOperator,
    GridInputRowSelectionModel,
    GridLogicOperator,
    GridRowSelectionModel,
    GridSortModel,
    gridSortModelSelector,
    useGridApiContext,
    useGridApiRef
} from '@mui/x-data-grid'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import React, {useEffect, useState} from 'react'
import {operatorMap} from './gridFilterOperators'
import './DaTXDataGrid.scss'
import {GridApiCommunity} from '@mui/x-data-grid/models/api/gridApiCommunity'
import {SortingDirection} from '../../search/model/Sorting'

type ColumnMenuProps = Partial<GridColumnMenuProps & ColumnMenuPropsOverrides>
type ColumnFilterIconButtonProps = Partial<
    ColumnHeaderFilterIconButtonProps & ColumnHeaderFilterIconButtonPropsOverrides
>
type Row = any

declare module '@mui/x-data-grid' {
    interface ColumnMenuPropsOverrides {
        setFilter: (filter: GridFilterItem) => void
        filters: GridFilterItem[]
        rows: Row[]
        handleSorting?: (params: GridColumnHeaderParams, direction: SortingDirection) => void,
        sortingModel?: GridSortModel
        handleFilterValue?: (params: GridColumnHeaderParams, matchString: string) => void,
    }

    interface ColumnHeaderFilterIconButtonPropsOverrides {
        setFilter: (filter: GridFilterItem) => void
        filters: GridFilterItem[]
    }
}

/*
    As we do not directly use MUI filter feature we need to compute the actual cell value if needed
*/
const getActualCellValue = (row: Row, colDef: any) => {
    return colDef.valueGetter ? colDef.valueGetter(undefined, row) : row[colDef.field]
}

const TableMenuFilterIcon = () => {
    return <FilterListIcon/>
}

/*
    The menu displayed for each column, can have a sort and filter section based on the column definition
*/
const DaTXColumnMenu = (props: NonNullable<ColumnMenuProps>) => {
    if (!props.colDef) {
        return <span>No column selected</span>
    }

    const {colDef: {sortable, filterable, filterOperators}} = props

    const activeFilters: GridFilterItem[] =
        props.filters?.find((filter: GridFilterItem) => filter.id === props.colDef?.field)?.value || []

    return (
        <div className="datagrid-sorting-panel">
            {sortable && <SortSection colDef={props.colDef}/>}
            {filterable && filterOperators?.length && props.setFilter && props.rows && (
                <FilterSection
                    rows={props.rows}
                    colDef={props.colDef}
                    activeFilters={activeFilters}
                    setFilter={props.setFilter}
                />
            )}
        </div>
    )
}

const ActiveFilterIcon = (props: NonNullable<ColumnFilterIconButtonProps>) => {
    if (!props?.field) {
        return <></>
    }

    const filter = props?.filters?.find((item: GridFilterItem) => item.field == props.field)

    const deleteFilter = (event: React.MouseEvent) => {
        event.stopPropagation()
        if (props.setFilter && filter) {
            props.setFilter({...filter, value: []})
        }
    }

    if (filter && filter?.value && filter?.value[0] !== '') {
        return (
            <IconButton className="active-filter-icon" onClick={deleteFilter}>
                <FilterListIcon/>
            </IconButton>
        )
    }
    return <></>
}

type SortSectionProps = {
    colDef: GridColDef,
    handleSorting?: (params: GridColumnHeaderParams, direction: SortingDirection) => void,
    sortingModel?: GridSortModel
}

const SortSection = ({colDef}: { colDef: GridColDef }) => {
    const apiRef = useGridApiContext()

    const sortingModel = gridSortModelSelector(apiRef)

    const isActiveSorting = (sortingOrder: string) =>
        sortingModel[0]?.field == colDef.field && sortingModel[0].sort === sortingOrder

    const onClick = (sortingDirection: SortingDirection) => {
        apiRef.current.sortColumn(colDef.field, isActiveSorting(sortingDirection) ? null : sortingDirection)
    }

    return (
        <>
            <h3>Sort</h3>
            <div className="sort-options">
                <Chip
                    icon={<ArrowUpwardIcon/>}
                    label="Ascending"
                    onClick={() => onClick('asc')}
                    sx={{boxShadow: 2}}
                    color={isActiveSorting('asc') ? 'primary' : undefined}
                />

                <Chip
                    icon={<ArrowDownwardIcon/>}
                    label="Descending"
                    onClick={() => onClick('desc')}
                    sx={{boxShadow: 2}}
                    color={isActiveSorting('desc') ? 'primary' : undefined}
                />
            </div>
        </>
    )
}

const FilterSection = ({
                           colDef,
                           rows,
                           activeFilters,
                           setFilter
                       }: {
    colDef: GridColDef
    rows: Row[]
    activeFilters: GridFilterItem[]
    setFilter?: (filter: GridFilterItem) => void
}) => {
    return (
        <>
            <h3>Filter</h3>
            {colDef.filterOperators?.map((filterOperator: GridFilterOperator) => {
                const FilterInput = (filterOperator.InputComponent || NoFilterInput) as any
                return (
                    <FilterInput
                        key={`${filterOperator.value}$${colDef.field}`}
                        colDef={colDef}
                        getPossibleValues={(colDef: GridColDef) =>
                            rows
                                .map((row) => getActualCellValue(row, colDef))
                                .filter((item, pos: number, array) => array.indexOf(item) === pos)
                                .sort()
                        }
                        filters={activeFilters}
                        setFilter={setFilter}
                        operator={filterOperator.value}
                    />
                )
            })}
        </>
    )
}

const DaTXDataGridActionHolder = ({
                                      resetFilterButton,
                                      children
                                  }: {
    resetFilterButton?: React.ReactNode
    children: React.ReactNode
}) => {
    return (
        <div className="datagrid-action-holder">
            {children}
            {resetFilterButton}
        </div>
    )
}

const isCustomFilter = (applyFilters: any) => {
    return applyFilters !== applyFiltersLocally
}

/*
    A wrapper around MUI DataGrid. Particularly to implement style and filtering features.
*/
const DaTXDataGrid = ({
                          columns,
                          rows,
                          getRowId,
                          applyFilters = applyFiltersLocally,
                          hideFooter = false,
                          getRowClassName,
                          getCellClassName,
                          displayActionBar = true,
                          checkboxSelection = false,
                          rowSelectionModel,
                          onRowSelectionModelChange,
                          noItemMessages = 'No rows',
                          children,
                          sortingModel,
                          onSortModelChange,
                          isLoading,
                          filterModel
                      }: {
    columns: GridColDef[]
    rows: Row[]
    getRowId: any
    applyFilters?: any
    hideFooter?: boolean
    getRowClassName?: DataGridProps['getRowClassName']
    getCellClassName?: DataGridProps['getCellClassName']
    displayActionBar?: boolean
    checkboxSelection?: boolean
    rowSelectionModel?: GridInputRowSelectionModel
    onRowSelectionModelChange?: (newSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void,
    noItemMessages?: string
    children?: React.ReactNode,
    sortingModel?: GridSortModel,
    onSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void,
    isLoading?: boolean,
    filterModel?: GridFilterModel
}) => {
    const apiRef = useGridApiRef()
    const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>(filterModel ?? {
        items: [],
        quickFilterLogicOperator: GridLogicOperator.And
    })

    useEffect(() => {
        setGridFilterModel((prevState: GridFilterModel) => {
            if (prevState === filterModel) return prevState
            return filterModel ?? {
                items: [],
                quickFilterLogicOperator: GridLogicOperator.And
            }
        })
    }, [filterModel])

    //For offline filtering, we need to keep track of the actually displayed rows but also of the full set of rows, in order to be able to build dynamic option lists for filters
    const [filteredRows, setFilteredRows] = useState<Row[]>(rows)

    const handleFilter = (filterItem: GridFilterItem) => {
        const definedFilterIndex = gridFilterModel.items.findIndex((existingItem) => existingItem.id == filterItem.id)

        //If we are dealing with a new filter
        if (definedFilterIndex == -1) {
            const updatedItems = [...gridFilterModel.items, filterItem]
            applyCustomFilters(updatedItems)
            setGridFilterModel({...gridFilterModel, items: updatedItems})
            return
        }

        //If we need to update an already defined filter (ex: add a possible value to a multi-select)
        if (filterItem.value?.length) {
            const updatedItems = [
                ...gridFilterModel.items.slice(0, definedFilterIndex),
                filterItem,
                ...gridFilterModel.items.slice(definedFilterIndex + 1)
            ]
            applyCustomFilters(updatedItems)
            setGridFilterModel({
                ...gridFilterModel,
                items: updatedItems
            })
            return
        }

        //Finally, if we get here, we actually need to remove the filter
        const updatedItems = [
            ...gridFilterModel.items.slice(0, definedFilterIndex),
            ...gridFilterModel.items.slice(definedFilterIndex + 1)
        ]
        applyCustomFilters(updatedItems)
        setGridFilterModel({
            ...gridFilterModel,
            items: updatedItems
        })
    }

    const applyCustomFilters = (items: GridFilterItem[]) => {
        if (isCustomFilter(applyFilters)) {
            applyFilters({filters: items, rows})
        }
    }

    useEffect(() => {
        if (!isCustomFilter(applyFilters)) {
            setFilteredRows(applyFiltersLocally({filters: gridFilterModel.items, columns, rows, apiRef}))
        }
    }, [gridFilterModel, columns, rows, apiRef])

    const CustomNoRowsOverlay = () => {
        return (
            <div className="no-data-message" style={{padding: 20, textAlign: 'center'}}>
                {noItemMessages}
            </div>
        )
    }

    return (
        <>
            {displayActionBar && <DaTXDataGridActionHolder children={children}/>}

            <DataGrid
                data-testid="datx-datagrid"
                apiRef={apiRef}
                autoHeight={true}
                columnHeaderHeight={70}
                getRowId={getRowId}
                rows={isCustomFilter(applyFilters) ? rows : filteredRows}
                columns={columns}
                hideFooter={hideFooter}
                filterModel={gridFilterModel}
                className="datx-datagrid"
                getRowClassName={getRowClassName}
                getCellClassName={getCellClassName}
                filterMode="server"
                checkboxSelection={checkboxSelection}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={onRowSelectionModelChange}
                disableRowSelectionOnClick
                keepNonExistentRowsSelected
                sortModel={sortingModel}
                onSortModelChange={onSortModelChange}
                loading={isLoading}
                slots={{
                    columnMenu: DaTXColumnMenu,
                    columnHeaderFilterIconButton: ActiveFilterIcon,
                    noRowsOverlay: CustomNoRowsOverlay
                }}
                slotProps={{
                    columnMenu: {
                        setFilter: handleFilter,
                        filters: gridFilterModel.items,
                        rows: rows
                    },
                    columnHeaderFilterIconButton: {
                        setFilter: handleFilter,
                        filters: gridFilterModel.items
                    }
                }}
                initialState={{
                    sorting: {
                        sortModel: sortingModel
                    }
                }}
            />
        </>
    )
}

/*
    By default, we need a way to apply filters localy on multiple Grid columns at once
*/
const applyFiltersLocally = ({
                                 filters,
                                 columns,
                                 rows,
                                 apiRef
                             }: {
    filters: GridFilterItem[]
    columns: GridColDef[]
    rows: Row[],
    apiRef: React.RefObject<GridApiCommunity>
}): Row[] => {
    /*
        First we build the complete list of filters
    */

    const filterFunctions = filters.map((filter) => {
        const column = columns.find((col) => filter.id === col.field)
        if (column) {
            const actualFilter = operatorMap[filter.operator].getApplyFilterFn(filter, column)
            if (actualFilter) {
                return (row: Row) =>
                    actualFilter(getActualCellValue(row, column), row, column, apiRef)
            }
        }
        return () => true
    })

    //Then we iterate once on rows and keep only rows matching all the defined filters
    return rows.filter((row) => filterFunctions.every((filter) => filter(row)))
}

const NoFilterInput = () => <div>No Filter defined for column</div>

export default DaTXDataGrid
